import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDesktopLayoutAsync,
  saveDesktopLayoutAsync,
  patchLayout,
  setLayout,
  resetLayout,
  selectLayout,
  selectLayoutLoaded,
} from "./desktopLayoutSlice";

const SAVE_DEBOUNCE_MS = 1200;
const RETRY_DELAYS_MS = [2000, 6000, 15000];

/**
 * useDesktopLayoutSync — loads the user's saved Webtop layout on mount and
 * provides debounced persistence helpers used by later phases:
 *  - updateLayout(partial): shallow-merge into the layout + autosave
 *  - replaceLayout(next):   replace the whole layout + autosave
 *
 * A failed load doesn't block the UI (the slice still marks `loaded`).
 */
export const useDesktopLayoutSync = () => {
  const dispatch = useDispatch();
  const layout = useSelector(selectLayout);
  const loaded = useSelector(selectLayoutLoaded);
  const saving = useSelector((s) => s?.desktopLayout?.saving);
  const saveError = useSelector((s) => s?.desktopLayout?.saveError);
  const userId = useSelector((s) => s?.loginUserInfo?.loginUserDetails?.id);

  // The user id we last loaded the layout for. Used to refetch when the logged-in
  // user changes within the same SPA session (logout → login without a full page
  // reload), so one user never inherits another's saved desktop.
  const loadedForUserRef = useRef(undefined);

  // Track the latest layout so debounced saves use fresh state, not a closure.
  const layoutRef = useRef(layout);
  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  const timerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryAttemptsRef = useRef(0);

  // Load the layout for the current user, and refetch if the user changes (the
  // backend keys the layout to the JWT identity — see getDesktopLayout). Without
  // this, after logout → login in the same session the new user would inherit the
  // previous user's in-memory layout (sticky notes, icons, pins, wallpaper, …).
  useEffect(() => {
    if (userId == null) return; // wait until we know who is logged in
    if (loadedForUserRef.current === userId) return; // already loaded for this user
    if (loadedForUserRef.current != null) dispatch(resetLayout()); // different user → drop stale
    loadedForUserRef.current = userId;
    dispatch(fetchDesktopLayoutAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const attemptSave = useCallback(
    (nextLayout, attempt = 0) => {
      dispatch(saveDesktopLayoutAsync(nextLayout)).then((action) => {
        if (saveDesktopLayoutAsync.fulfilled.match(action)) {
          retryAttemptsRef.current = 0;
        } else if (attempt < RETRY_DELAYS_MS.length) {
          // Retry with increasing delay on failure
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            attemptSave(layoutRef.current, attempt + 1);
          }, RETRY_DELAYS_MS[attempt]);
        }
      });
    },
    [dispatch] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const scheduleSave = useCallback(
    (nextLayout) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      retryAttemptsRef.current = 0;
      timerRef.current = setTimeout(() => {
        attemptSave(nextLayout, 0);
      }, SAVE_DEBOUNCE_MS);
    },
    [attemptSave]
  );

  const updateLayout = useCallback(
    (partial) => {
      const next = { ...layoutRef.current, ...(partial || {}) };
      dispatch(patchLayout(partial || {}));
      scheduleSave(next);
    },
    [dispatch, scheduleSave]
  );

  const replaceLayout = useCallback(
    (next) => {
      dispatch(setLayout(next || {}));
      scheduleSave(next || {});
    },
    [dispatch, scheduleSave]
  );

  // Persist the current layout to the dashboard (backend) immediately, flushing
  // the pending debounced autosave. Used by an explicit "Save" action.
  const saveNow = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    dispatch(saveDesktopLayoutAsync(layoutRef.current));
  }, [dispatch]);

  // Flush/cleanup pending saves and retries on unmount.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    []
  );

  return { layout, loaded, saving, saveError, updateLayout, replaceLayout, saveNow };
};

export default useDesktopLayoutSync;
